import { PHONE_CONSENT_TEXT, type PhoneVerifyError } from "@bash/shared";

/**
 * Every string Bash's phone shows (spec 2026-10-14 §0, §6.2; the mock
 * mockups/bash-phone-2026-10-14.html). Bash's words, sentence case, no
 * dashes, never a vendor.
 */
export const PHONE_COPY = {
  title: "Bash's phone",
  lede: "Call Bash from anywhere. He knows your day and your team.",
  unavailable: "Bash's phone isn't available yet.",
  // A paid plan holds Bash's number (founder 2026-10-17)
  plan: "Your plan",
  needsPlan: "Bash's phone comes with any paid plan.",
  pausedUntil: (date: string): string =>
    `Bash's number is paused while you're on the free plan. Pick a paid plan by ${date} to keep it.`,
  seePlans: "See plans",
  // The first call: onboarding, the first-open ask, and the page with no number yet
  askTitle: "I have a phone now.",
  ask: "What number should I call? I'll ring you once, so you have mine.",
  numberLabel: "Your number",
  numberPlaceholder: "+1 415 555 0100",
  suggestedHint: "The number you chat with me on.",
  consent: PHONE_CONSENT_TEXT,
  callMe: "Call me",
  notNow: "Not now",
  save: "Save my number",
  saveWhy: "Save it first, so my call rings through.",
  calling: (bashNumber: string): string =>
    `I'll call you in a few seconds from ${bashNumber}.`,
  ringing: "Calling you now",
  keyIn: "When it rings, key in this code:",
  verified: "You have my number now. Call me anytime.",
  /** The answer to a number already theirs (409 already_verified): nothing to do, and that is good news. */
  alreadyYours: "You're all set. You have my number.",
  pinAsk: "One more thing: pick a PIN, for when I can't tell it's you.",
  pinField: "PIN",
  continue: "Continue",
  failed: "That didn't work. Try again in a moment.",
  tryAgain: "Try again",
  errors: {
    bad_number: "That number doesn't look right. Include the country code.",
    plan_required: "Bash's phone comes with any paid plan.",
    country_not_open: "I can call numbers in the US and India for now.",
    already_verified: "That number is already yours.",
    number_taken: "That number belongs to another account.",
    daily_cap: "Bash has called enough times today. Try again tomorrow.",
    lifetime_cap: "Bash can't place more verification calls for this account.",
    number_blocked: "That number failed twice. Try another one.",
    line_type_refused: "Bash can't call that kind of number.",
    lookup_failed: "Bash couldn't check that number. Try again in a moment.",
    pool_empty: "Bash is getting his number ready. Try again in a few minutes.",
    dial_failed: "Bash couldn't place the call. Try again in a moment.",
    call_in_progress: "Bash is already calling you.",
    unknown: "Something went wrong. Try again in a moment.",
  } satisfies Record<PhoneVerifyError | "unknown", string>,
  // The first open after launch (spec §3.1)
  offerTitle: "I have a phone now. Want my number?",
  offerLede: "I'll ring you once, so you have mine.",
  yes: "Yes",
  numberTitle: "What number should I call?",
  // The page
  hisNumber: "His number",
  yourNumbers: "Your numbers",
  verifiedOn: (date: string): string => `Verified ${date}`,
  addNumber: "Add a number",
  addHint: "Bash rings it once with a code.",
  remove: "Remove",
  pin: "Your PIN",
  pinLede: "When Bash can't tell it's you, he asks for this on the keypad.",
  pinLabel: "New PIN",
  pinPlaceholder: "4 to 6 digits",
  setPin: "Set PIN",
  changePin: "Change PIN",
  pinBad: "A PIN is 4 to 6 digits.",
  pinSaved: "PIN saved.",
  locked: "Calls Bash can't verify are locked after too many wrong PINs.",
  unlock: "Unlock",
  voice: "His voice",
  voiceLede: "His voice on the phone and in the app, from the next call.",
  sample: "“Hi, I'm Bash.”",
  play: (voice: string): string => `Play ${voice}`,
  history: "Calls",
  historyEmpty: "No calls yet.",
  youCalled: "You called",
  bashCalled: "Bash called",
  you: "You",
  bash: "Bash",
} as const;

/**
 * A number grouped for reading aloud: "+1 202 555 0101", "+91 98765 43210".
 * Anything else is shown as it came.
 */
export function displayNumber(e164: string): string {
  const us = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  if (us) return `+1 ${us[1]} ${us[2]} ${us[3]}`;
  const ind = /^\+91(\d{5})(\d{5})$/.exec(e164);
  if (ind) return `+91 ${ind[1]} ${ind[2]}`;
  return e164;
}
